import { createRouter, createWebHashHistory } from "vue-router";
import { useApiResources } from "~/stores/ApiResources";
import { useAppTabs } from "~/stores/useAppTabs";
import Dashboard from "~/views/dashboard.vue";
import DefaultCreate from "~/views/default.create.vue";
import DefaultIndex from "~/views/default.index.vue";
import DefaultUpdate from "~/views/default.update.vue";

const viewComponents = import.meta.glob<Component>(["../views/*.vue"]);
const viewRoutes = Object.entries(viewComponents).map(([cmpPath, cmpImport]) => {
	// The name as we get it is something like ../views/default.create.vue
	// We want to remove the .vue part and replace the . with a / for the path so we get /default/create
	// We also want to remove ../views/ from the path
	const viewName = cmpPath.replace(".vue", "").replace("../views/", "");
	const viewPath = `/${viewName.replace(".", "/").replace(/\/update$/, "/update/:id")}`;

	let tabTitle = "";
	if (viewName.includes("update")) {
		tabTitle = "Bewerken";
	} else if (viewName.includes("create")) {
		tabTitle = "Aanmaken";
	} else {
		tabTitle = "Overzicht";
	}

	return {
		path: viewPath,
		name: viewName,
		component: cmpImport,
		meta: { tabTitle },
		props: (route: any) => {
			return {
				resourceId: route.params.id || undefined,
				...route.query,
			};
		},
	};
});

const router = createRouter({
	history: createWebHashHistory(),
	routes: [
		{
			/** catch all */
			path: "/:pathMatch(.*)*",
			name: "not-found",
			meta: { tabTitle: "404" },
			component: () => import("~/views/404.vue"),
		},
		{
			path: "/",
			name: "home",
			meta: { tabTitle: "Dashboard" },
			component: Dashboard,
		},
		{
			path: "/:resource/index",
			component: DefaultIndex,
			meta: { tabTitle: "Overzicht" },
			props: (route: any) => {
				const { findByName } = useApiResources();
				const resource = findByName(route.params.resource);

				return {
					resourceKey: resource.name,
					resourceUrl: resource.uri,
					resourceName: resource.title,
					gridName: resource.name,
					...route.query,
				};
			},
		},
		{
			path: "/:resource/create",
			component: DefaultCreate,
			meta: { tabTitle: "Aanmaken" },
			props: (route: any) => {
				const { findByName } = useApiResources();
				const resource = findByName(route.params.resource);
				return {
					resourceKey: resource.name,
					resourceUrl: resource.uri,
					resourceName: resource.title,
					gridName: resource.name,
					...route.query,
				};
			},
		},
		{
			path: "/:resource/update/:id",
			component: DefaultUpdate,
			meta: { tabTitle: "Bewerken" },
			props: (route: any) => {
				const { findByName } = useApiResources();
				const resource = findByName(route.params.resource);
				return {
					resourceId: route.params.id,
					resourceKey: resource.name,
					resourceUrl: resource.uri,
					resourceName: resource.title,
					gridName: resource.name,
					...route.query,
				};
			},
		},
		...viewRoutes,
	],
});

const tabExcludedRouteNames = ["not-found", "home"];

router.beforeEach((to, _, next) => {
	const { addTab, sessionTabs } = useAppTabs();

	// If the exact tab is already open, navigate to it
	if (sessionTabs.value.find((tab) => tab.fullPath === to.fullPath)) {
		return next();
	}
	// If the tab is an update or create tab then open the existing tab if it exists
	if (to.fullPath.includes("update") || to.fullPath.includes("create")) {
		const existingTab = sessionTabs.value.find((tab) => tab.fullPath.includes(to.path));

		if (existingTab) {
			return next(existingTab.fullPath);
		}
	}

	// If the route is in the excluded list, don't add it to the tabs
	if (!tabExcludedRouteNames.includes(to.name as string)) {
		addTab({
			title: (to.meta?.tabTitle as string) || (to.name as string) || "Nieuw tabblad",
			fullPath: to.fullPath,
			pinned: Boolean(to.query.pinned),
		});
	}

	return next();
});

export default router;
